import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, firstValueFrom, map, of, throwError } from 'rxjs';
import { JsonHttpService } from '../../shared/http/json-http.service';
import { AbsenceConfigurationPresentation } from '../models/absence-configuration-presentation';
import { AlertConfigurationStatus } from '../models/alert-configuration';
import { AlertEvent } from '../models/alert-event';

@Injectable({
    providedIn: 'root'
})
export class AlertService {
    private readonly httpClient = inject(JsonHttpService);

    async getAlertConfigurationStatuses(): Promise<AlertConfigurationStatus[]> {
        return await firstValueFrom(this.httpClient.getTyped<AlertConfigurationStatus[]>('/api/alert/get-alertconfigurationstatuses'));
    }

    async enableAlertConfigurations(alertConfigurationIds: number[]): Promise<void> {
        await firstValueFrom(this.httpClient.post('/api/alert/enable-alertconfigurations', alertConfigurationIds, null));
    }

    async disableAlertConfigurations(alertConfigurationIds: number[]): Promise<void> {
        await firstValueFrom(this.httpClient.post('/api/alert/disable-alertconfigurations', alertConfigurationIds, null));
    }

    async cancelAlerts(alertConfigurationIds: number[]): Promise<void> {
        await firstValueFrom(this.httpClient.post('/api/alert/cancel-alerts', alertConfigurationIds, null));
    }

    async getAlertEventsForAlertConfiguration(alertConfigurationId: number): Promise<AlertEvent[]> {
        const options = { params: new HttpParams().set('alertConfigurationId', alertConfigurationId.toString()) };
        return await firstValueFrom(this.httpClient.getTyped<AlertEvent[]>('/api/alert/show-history', options));
    }

    getAlertStatusTooltip(latestAlertStatus: string): string {
        let tooltip = '';
        if (latestAlertStatus === 'Failed') {
            tooltip = 'No activity since last alert notification!';
        } else if (latestAlertStatus === 'Ok') {
            tooltip = 'OK';
        } else if (latestAlertStatus === 'None') {
            tooltip = 'No activity';
        }
        return tooltip;
    }

    async getGlobalAlertAbsence(): Promise<AbsenceConfigurationPresentation> {
        return await firstValueFrom(
            this.httpClient
                .getWithErrorEval('/api/alert/get-global-alert-absence', undefined, (err: HttpErrorResponse) => err?.status !== 403)
                .pipe(
                    catchError((err: HttpErrorResponse) => {
                        if (err?.status === 403) {
                            return of<AbsenceConfigurationPresentation>({ FromDateTime: null, ToDateTime: null, Note: '' });
                        }
                        return throwError(( ) => err);
                    }),
                    map((absence: AbsenceConfigurationPresentation) => {
                        if (!absence) {
                            absence = { FromDateTime: null, ToDateTime: null, Note: '' };
                        }
                        else {
                            absence.FromDateTime = this.toDate(absence.FromDateTime);
                            absence.ToDateTime = this.toDate(absence.ToDateTime);
                            absence.Note = absence.Note ?? '';
                        }
                        return absence;
                    })
                )
        );
    }

    async setGlobalAlertAbsence(absence: AbsenceConfigurationPresentation): Promise<AbsenceConfigurationPresentation> {
        return await firstValueFrom(
            this.httpClient
                .postTyped<AbsenceConfigurationPresentation>('/api/alert/set-global-alert-absence', absence)
                .pipe(map((responseAbsence: AbsenceConfigurationPresentation) => {
                    responseAbsence.FromDateTime = this.toDate(responseAbsence.FromDateTime);
                    responseAbsence.ToDateTime = this.toDate(responseAbsence.ToDateTime);
                    return responseAbsence;
                }))
        );
    }

    private toDate(value: unknown): Date | null {
        if (value === null || value === undefined) {
            return null;
        }

        let parsedDate: Date;
        if (value instanceof Date) {
            parsedDate = value;
        } else if (typeof value === 'string' || typeof value === 'number') {
            parsedDate = new Date(value);
        } else {
            throw new Error('Invalid absence date value.');
        }

        if (Number.isNaN(parsedDate.getTime())) {
            throw new Error('Invalid absence date value (NaN).');
        }

        return parsedDate;
    }
}
